import { getJson, postJson, requestJson } from './client';
import type { StudioDetail } from './partnerStudios';

// --- Types (admin StudioApprovalController — /internal/admin/spaces/studio-approvals) ---

export type StudioApprovalStatus = 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';

export type AdminStudioApprovalSummary = {
  studioId: string;
  name: string;
  ownerUserId: string;
  approvalStatus: StudioApprovalStatus | string;
  submittedAt: string | null;
  primaryImageUrl: string | null;
  roadAddress: string | null;
};

/** 점주 본인/admin 상세에만 노출되는 필드 포함 StudioDetailResponse. */
export type AdminStudioDetail = StudioDetail & {
  businessLicenseImageRef?: string | null;
  businessLicenseImageUrl?: string | null;
  approvalStatus?: StudioApprovalStatus | string;
  approvalRejectedReason?: string | null;
};

export type AdminStudioApprovalDetail = {
  studio: AdminStudioDetail;
  approvalStatus: StudioApprovalStatus | string;
  submittedAt: string | null;
  decidedAt: string | null;
  rejectedReason: string | null;
  ownerUserId: string;
  ownerNickname: string | null;
  ownerEmail: string | null;
};

export type AdminStudioApprovalDecision = {
  studioId: string;
  approvalStatus: StudioApprovalStatus | string;
  submittedAt: string | null;
  decidedAt: string | null;
  rejectedReason: string | null;
};

// --- Types (admin VendorRegistrationAdminController — /internal/admin/search/registration-requests) ---

export type AdminVendorRegistrationRequest = {
  requestKey: string;
  externalPlaceId: string | null;
  vendorName: string;
  roadAddress: string | null;
  source: string;
  requestCount: number;
  active: boolean;
  firstRequestedAt: string;
  lastRequestedAt: string;
};

export type AdminVendorRegistrationRequestList = {
  items: AdminVendorRegistrationRequest[];
  page: number;
  size: number;
  totalCount: number;
};

// --- Functions ---

export function getRegistrationRequests(
  activeOnly = true,
  page = 0,
  size = 20,
): Promise<AdminVendorRegistrationRequestList> {
  return getJson<AdminVendorRegistrationRequestList>(
    `/internal/admin/search/registration-requests?activeOnly=${activeOnly}&page=${page}&size=${size}`,
  );
}

export function getStudioApprovals(status: StudioApprovalStatus = 'PENDING') {
  return getJson<AdminStudioApprovalSummary[]>(
    `/internal/admin/spaces/studio-approvals?status=${status}`,
  );
}

export function getStudioApprovalDetail(studioId: string) {
  return getJson<AdminStudioApprovalDetail>(
    `/internal/admin/spaces/studio-approvals/${encodeURIComponent(studioId)}`,
  );
}

export function approveStudio(studioId: string) {
  return requestJson<AdminStudioApprovalDecision>(
    `/internal/admin/spaces/studio-approvals/${encodeURIComponent(studioId)}/approve`,
    { method: 'POST' },
  );
}

export function rejectStudio(studioId: string, reason: string) {
  return postJson<AdminStudioApprovalDecision>(
    `/internal/admin/spaces/studio-approvals/${encodeURIComponent(studioId)}/reject`,
    { reason },
  );
}
